import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Building, MapPin, Briefcase, Save, Plus, Trash2, ArrowLeft } from 'lucide-react';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDUSTRY_TYPES = [
  'Services',
  'Manufacturing',
  'IT & Software',
  'Healthcare',
  'Education',
  'Retail',
  'Construction',
  'Hospitality',
  'Banking & Finance',
  'Automotive',
  'Textiles',
  'Food & Beverage',
  'Logistics',
  'Agriculture',
  'Other'
];

const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
];

const EmployerProfilePage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [profile, setProfile] = useState({
    company_logo_url: '',
    company_name: '',
    industry_type: '',
    address: '',
    city: '',
    tehsil: '',
    district: '',
    state: '',
    pin_code: '',
    interested_naps_nats: '',
    locations: []
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  // Fetch districts when state changes
  useEffect(() => {
    if (profile.state) {
      fetchDistricts(profile.state);
    } else {
      setDistricts([]);
    }
  }, [profile.state]);

  const fetchDistricts = async (state) => {
    setLoadingDistricts(true);
    try {
      const response = await axios.get(`${API}/districts/${encodeURIComponent(state)}`);
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
    setLoadingDistricts(false);
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/employer`);
      if (response.data) {
        setProfile({
          company_logo_url: response.data.company_logo_url || '',
          company_name: response.data.company_name || '',
          industry_type: response.data.industry_type || '',
          address: response.data.address || '',
          city: response.data.city || '',
          tehsil: response.data.tehsil || '',
          district: response.data.district || '',
          state: response.data.state || '',
          pin_code: response.data.pin_code || '',
          interested_naps_nats: response.data.interested_naps_nats || '',
          locations: response.data.locations || []
        });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!profile.company_name) {
      toast.error('Company name is required');
      return;
    }
    if (!profile.industry_type) {
      toast.error('Industry type is required');
      return;
    }
    if (!profile.address) {
      toast.error('Address is required');
      return;
    }
    if (!profile.city) {
      toast.error('City is required');
      return;
    }
    if (!profile.state) {
      toast.error('State is required');
      return;
    }
    if (!profile.interested_naps_nats) {
      toast.error('Please select NAPS/NATS interest');
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`${API}/profile/employer`, profile);
      toast.success('Profile updated successfully!');
      navigate('/employer/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const addLocation = () => {
    setProfile({
      ...profile,
      locations: [
        ...profile.locations,
        { city: '', state: '', address: '' }
      ]
    });
  };

  const removeLocation = (index) => {
    const newLocations = profile.locations.filter((_, i) => i !== index);
    setProfile({ ...profile, locations: newLocations });
  };

  const updateLocation = (index, field, value) => {
    const newLocations = [...profile.locations];
    newLocations[index] = { ...newLocations[index], [field]: value };
    setProfile({ ...profile, locations: newLocations });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-12 w-12 border-4 border-emerald-600 border-t-transparent rounded-full"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Button variant="ghost" onClick={() => navigate('/employer/dashboard')} className="mb-2">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">Company Profile</h1>
            <p className="text-slate-600">Complete your company profile to post job opportunities</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          {/* Company Logo */}
          <div className="mb-6">
            <Label className="text-base font-semibold mb-2 block">Company Logo</Label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300">
                {profile.company_logo_url ? (
                  <img src={profile.company_logo_url} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <Building className="h-10 w-10 text-slate-400" />
                )}
              </div>
              <div className="flex-1">
                <Input
                  placeholder="Enter logo URL"
                  value={profile.company_logo_url}
                  onChange={(e) => setProfile({ ...profile, company_logo_url: e.target.value })}
                  data-testid="company-logo-input"
                />
                <p className="text-xs text-slate-500 mt-1">Enter a URL for your company logo</p>
              </div>
            </div>
          </div>

          {/* Company Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                placeholder="Enter company name"
                value={profile.company_name}
                onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                className="mt-1"
                data-testid="company-name-input"
              />
            </div>
            <div>
              <Label htmlFor="industry_type">Industry Type *</Label>
              <Select
                value={profile.industry_type}
                onValueChange={(value) => setProfile({ ...profile, industry_type: value })}
              >
                <SelectTrigger className="mt-1" data-testid="industry-type-select">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRY_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Address */}
          <div className="mb-6">
            <Label className="text-base font-semibold mb-2 block flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Registered Address
            </Label>
            <Textarea
              placeholder="Complete address"
              value={profile.address}
              onChange={(e) => setProfile({ ...profile, address: e.target.value })}
              className="mb-3"
              data-testid="address-input"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  placeholder="City"
                  value={profile.city}
                  onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                  className="mt-1"
                  data-testid="city-input"
                />
              </div>
              <div>
                <Label htmlFor="state">State *</Label>
                <Select
                  value={profile.state}
                  onValueChange={(value) => setProfile({ ...profile, state: value, district: '' })}
                >
                  <SelectTrigger className="mt-1" data-testid="state-select">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATES.map((state) => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="district">District</Label>
                <Select
                  value={profile.district}
                  onValueChange={(value) => setProfile({ ...profile, district: value })}
                  disabled={!profile.state || loadingDistricts}
                >
                  <SelectTrigger className="mt-1" data-testid="district-select">
                    <SelectValue placeholder={loadingDistricts ? 'Loading...' : 'Select district'} />
                  </SelectTrigger>
                  <SelectContent>
                    {districts.map((district) => (
                      <SelectItem key={district} value={district}>{district}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="tehsil">Tehsil</Label>
                <Input
                  id="tehsil"
                  placeholder="Tehsil"
                  value={profile.tehsil}
                  onChange={(e) => setProfile({ ...profile, tehsil: e.target.value })}
                  className="mt-1"
                  data-testid="tehsil-input"
                />
              </div>
            </div>
            <div className="mt-3">
              <Label htmlFor="pin_code">PIN Code</Label>
              <Input
                id="pin_code"
                placeholder="6-digit PIN"
                value={profile.pin_code}
                onChange={(e) => setProfile({ ...profile, pin_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                maxLength={6}
                className="mt-1 w-32"
                data-testid="pincode-input"
              />
            </div>
          </div>

          {/* NAPS/NATS Interest */}
          <div className="mb-6">
            <Label className="text-base font-semibold mb-2 block flex items-center gap-2">
              <Briefcase className="h-4 w-4" /> NAPS/NATS Interest *
            </Label>
            <p className="text-sm text-slate-600 mb-3">
              Are you interested in participating in National Apprenticeship Promotion Scheme (NAPS) or 
              National Apprenticeship Training Scheme (NATS)?
            </p>
            <Select
              value={profile.interested_naps_nats}
              onValueChange={(value) => setProfile({ ...profile, interested_naps_nats: value })}
            >
              <SelectTrigger className="w-48" data-testid="naps-nats-select">
                <SelectValue placeholder="Select option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes, Interested</SelectItem>
                <SelectItem value="No">No, Not Interested</SelectItem>
                <SelectItem value="Maybe">Maybe Later</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Additional Locations */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Label className="text-base font-semibold flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Additional Locations
                </Label>
                <p className="text-sm text-slate-600">Add other locations where your company operates</p>
              </div>
              <Button variant="outline" size="sm" onClick={addLocation} data-testid="add-location-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Location
              </Button>
            </div>
            
            {profile.locations.length === 0 ? (
              <div className="bg-slate-50 rounded-lg p-4 text-center text-slate-500">
                No additional locations added
              </div>
            ) : (
              <div className="space-y-3">
                {profile.locations.map((loc, index) => (
                  <div key={index} className="bg-slate-50 rounded-lg p-4 relative">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                      onClick={() => removeLocation(index)}
                      data-testid={`remove-location-${index}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Input
                        placeholder="City"
                        value={loc.city}
                        onChange={(e) => updateLocation(index, 'city', e.target.value)}
                        data-testid={`location-city-${index}`}
                      />
                      <Select
                        value={loc.state}
                        onValueChange={(value) => updateLocation(index, 'state', value)}
                      >
                        <SelectTrigger data-testid={`location-state-${index}`}>
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent>
                          {STATES.map((state) => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Address"
                        value={loc.address}
                        onChange={(e) => updateLocation(index, 'address', e.target.value)}
                        data-testid={`location-address-${index}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => navigate('/employer/dashboard')}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-profile-btn"
            >
              {isSaving ? 'Saving...' : (
                <>
                  <Save className="h-4 w-4 mr-2" /> Save Profile
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EmployerProfilePage;
